import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, FileText, Loader2, Plus, Stethoscope, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/app/components/ui/button'
import { CatalogSelect } from '@/app/components/ui/CatalogSelect'
import { Checkbox } from '@/app/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import { CartItem, CartPrescription, Doctor, PosCartData, posCartService } from '@/app/services/pos.service'
import { NewDoctorModal } from './NewDoctorModal'

export interface PrescriptionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  items: CartItem[]
  prescriptions: CartPrescription[]
  onSaved: (cart: PosCartData) => void
}

type PrescriptionDraft = {
  key: string
  id?: number
  doctor: Doctor | null
  prescriptionDate: string
  fulfillmentType: 'PARTIAL' | 'COMPLETE'
  controlledFolio: string
  cartItemIds: number[]
}

type ApiValidationError = {
  response?: { data?: { errors?: Record<string, string[]> } }
}

const cartItemId = (item: CartItem) => Number(item.cart_item_id ?? item.id)

export function PrescriptionModal({
  open,
  onOpenChange,
  items,
  prescriptions,
  onSaved,
}: PrescriptionModalProps) {
  const queryClient = useQueryClient()
  const draftSequence = useRef(0)
  const regulatedItems = useMemo(() => items.filter((item) => item.prescriptionRequired), [items])
  const [drafts, setDrafts] = useState<PrescriptionDraft[]>([])
  const [activeKey, setActiveKey] = useState('')
  const [showNewDoctor, setShowNewDoctor] = useState(false)

  const newDraft = (): PrescriptionDraft => {
    draftSequence.current += 1

    return {
      key: `new-${draftSequence.current}`,
      doctor: null,
      prescriptionDate: '',
      fulfillmentType: 'COMPLETE',
      controlledFolio: '',
      cartItemIds: [],
    }
  }

  useEffect(() => {
    if (!open) return

    const initialDrafts = prescriptions.length > 0
      ? prescriptions.map((prescription) => ({
        key: `prescription-${prescription.id}`,
        id: prescription.id,
        doctor: prescription.doctor,
        prescriptionDate: prescription.prescription_date || '',
        fulfillmentType: prescription.fulfillment_type,
        controlledFolio: prescription.controls.find(
          (control) => control.regulatory_class === 'CONTROLLED',
        )?.folio || '',
        cartItemIds: prescription.cart_item_ids.map(Number),
      }))
      : [newDraft()]

    setDrafts(initialDrafts)
    setActiveKey(initialDrafts[0].key)
  }, [open, prescriptions])

  const activeDraft = drafts.find((draft) => draft.key === activeKey) || drafts[0]
  const assignedIds = useMemo(
    () => new Set(drafts.flatMap((draft) => draft.cartItemIds)),
    [drafts],
  )
  const unassignedCount = regulatedItems.filter((item) => !assignedIds.has(cartItemId(item))).length
  const assignedToOtherDraft = useMemo(() => {
    const ids = new Set<number>()
    drafts.forEach((draft) => {
      if (draft.key !== activeDraft?.key) draft.cartItemIds.forEach((id) => ids.add(id))
    })
    return ids
  }, [activeDraft?.key, drafts])
  const selectableItems = regulatedItems.filter((item) => !assignedToOtherDraft.has(cartItemId(item)))
  const activeItems = activeDraft
    ? regulatedItems.filter((item) => activeDraft.cartItemIds.includes(cartItemId(item)))
    : []
  const hasControlled = activeItems.some((item) => item.regulatoryClass === 'CONTROLLED')
  const hasAntibiotic = activeItems.some((item) => item.regulatoryClass === 'ANTIBIOTIC')

  const updateActiveDraft = (changes: Partial<PrescriptionDraft>) => {
    if (!activeDraft) return
    setDrafts((current) => current.map((draft) => (
      draft.key === activeDraft.key ? { ...draft, ...changes } : draft
    )))
  }

  const addDraft = () => {
    if (unassignedCount === 0) return
    const draft = newDraft()
    setDrafts((current) => [...current, draft])
    setActiveKey(draft.key)
  }

  const removeDraft = (key: string) => {
    if (drafts.length === 1) return
    const index = drafts.findIndex((draft) => draft.key === key)
    const nextDrafts = drafts.filter((draft) => draft.key !== key)
    setDrafts(nextDrafts)
    if (activeKey === key) {
      setActiveKey(nextDrafts[Math.max(0, index - 1)]?.key || nextDrafts[0].key)
    }
  }

  const saveMutation = useMutation({
    mutationFn: posCartService.upsertPrescriptions,
    onSuccess: (cart) => {
      queryClient.setQueryData(['pos-cart'], cart)
      toast.success(drafts.length === 1 ? 'Receta asociada a la venta.' : 'Recetas asociadas a la venta.')
      onSaved(cart)
    },
    onError: (error: unknown) => {
      const errors = (error as ApiValidationError).response?.data?.errors
      toast.error(errors ? Object.values(errors)[0]?.[0] : 'No se pudieron guardar las recetas.')
    },
  })

  const save = () => {
    if (unassignedCount > 0) {
      toast.error(`Asigne los ${unassignedCount} productos regulados que faltan.`)
      return
    }

    for (let index = 0; index < drafts.length; index += 1) {
      const draft = drafts[index]
      if (draft.cartItemIds.length === 0) {
        toast.error(`La Receta ${index + 1} debe contener al menos un producto.`)
        setActiveKey(draft.key)
        return
      }
      if (!draft.doctor) {
        toast.error(`Seleccione el médico de la Receta ${index + 1}.`)
        setActiveKey(draft.key)
        return
      }
      const containsControlled = regulatedItems.some((item) => (
        draft.cartItemIds.includes(cartItemId(item)) && item.regulatoryClass === 'CONTROLLED'
      ))
      if (draft.fulfillmentType === 'COMPLETE' && containsControlled && !draft.controlledFolio.trim()) {
        toast.error(`Capture el folio controlado de la Receta ${index + 1}.`)
        setActiveKey(draft.key)
        return
      }
    }

    saveMutation.mutate({
      prescriptions: drafts.map((draft) => ({
        id: draft.id || null,
        doctor_id: draft.doctor!.id,
        prescription_date: draft.prescriptionDate || null,
        fulfillment_type: draft.fulfillmentType,
        controlled_folio: draft.fulfillmentType === 'COMPLETE'
          ? draft.controlledFolio.trim().toUpperCase() || null
          : null,
        cart_item_ids: draft.cartItemIds,
      })),
    })
  }

  if (!activeDraft) return null

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Recetas médicas de la venta</DialogTitle>
            <DialogDescription>
              Asigne cada producto regulado a la receta física que le corresponde.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-end gap-1 overflow-x-auto border-b border-slate-200 pt-1">
            {drafts.map((draft, index) => (
              <div
                key={draft.key}
                className={`flex shrink-0 items-center rounded-t-md border border-b-0 px-1 ${activeDraft.key === draft.key
                    ? 'border-slate-300 bg-white text-slate-950'
                    : 'border-transparent bg-slate-100 text-slate-600'
                  }`}
              >
                <button
                  type="button"
                  className="px-3 py-2 text-sm font-medium"
                  onClick={() => setActiveKey(draft.key)}
                >
                  Receta {index + 1}
                </button>
                {drafts.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Eliminar Receta ${index + 1}`}
                    className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    onClick={() => removeDraft(draft.key)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mb-1 shrink-0"
              onClick={addDraft}
              disabled={unassignedCount === 0}
            >
              <Plus className="mr-1 h-4 w-4" /> Añadir otra receta
            </Button>
          </div>

          <div className="space-y-5">
            <div className={`rounded-md border px-3 py-2 text-sm ${unassignedCount === 0
                ? 'border-green-200 bg-green-50 text-green-900'
                : 'border-orange-200 bg-orange-50 text-orange-950'
              }`}>
              {unassignedCount === 0
                ? 'Todos los productos regulados están asignados.'
                : `${unassignedCount} ${unassignedCount === 1 ? 'producto regulado pendiente' : 'productos regulados pendientes'} de asignar.`}
            </div>

            <section className="rounded-lg border border-orange-200 bg-orange-50 p-4">
              <div className="mb-3 flex items-center gap-2 font-medium text-orange-950">
                <FileText className="h-4 w-4" /> Productos cubiertos por esta receta
              </div>
              <div className="space-y-2">
                {selectableItems.map((item) => {
                  const id = cartItemId(item)
                  const selected = activeDraft.cartItemIds.includes(id)

                  return (
                    <label
                      key={id}
                      htmlFor={`prescription-${activeDraft.key}-item-${id}`}
                      className="flex cursor-pointer items-center gap-3 rounded-md bg-white px-3 py-2 text-sm"
                    >
                      <Checkbox
                        id={`prescription-${activeDraft.key}-item-${id}`}
                        checked={selected}
                        onCheckedChange={(checked) => updateActiveDraft({
                          cartItemIds: checked === true
                            ? [...activeDraft.cartItemIds, id]
                            : activeDraft.cartItemIds.filter((itemId) => itemId !== id),
                        })}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900">{item.name}</p>
                        <p className="text-xs text-slate-500">
                          {item.regulatoryClass === 'CONTROLLED'
                            ? 'Controlado'
                            : item.regulatoryClass === 'ANTIBIOTIC'
                              ? 'Antibiótico'
                              : 'Requiere receta'}
                        </p>
                      </div>
                      <span className="font-semibold text-slate-700">× {item.quantity}</span>
                    </label>
                  )
                })}
              </div>
            </section>

            <section className="space-y-3">
              <label className="text-sm font-medium text-slate-700">
                Médico <span className="text-red-600">*</span>
              </label>
              <div className="flex gap-2">
                <CatalogSelect
                  catalog="doctors"
                  value={activeDraft.doctor?.id}
                  selectedItem={activeDraft.doctor || undefined}
                  onChange={(doctor: Doctor | '') => updateActiveDraft({ doctor: doctor || null })}
                  placeholder="Buscar por nombre, cédula o especialidad"
                  returnObject
                  className="flex-1"
                  itemLabelFormatter={(doctor: Doctor) => `${doctor.full_name} · Cédula ${doctor.professional_license}`}
                />
                <Button type="button" variant="outline" onClick={() => setShowNewDoctor(true)}>
                  <Plus className="mr-2 h-4 w-4" />Nuevo
                </Button>
              </div>
            </section>

            {activeDraft.doctor && (
              <div className="flex gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-slate-700">
                <Stethoscope className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="font-medium text-slate-900">{activeDraft.doctor.full_name}</p>
                  <p>Cédula: {activeDraft.doctor.professional_license}</p>
                  <p>{activeDraft.doctor.address_line1}, {activeDraft.doctor.city}, {activeDraft.doctor.state}</p>
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha de receta <span className="text-xs font-normal text-slate-500">(opcional)</span>
                </label>
                <Input
                  type="date"
                  value={activeDraft.prescriptionDate}
                  onChange={(event) => updateActiveDraft({ prescriptionDate: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Tipo de surtido <span className="text-red-600">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(['PARTIAL', 'COMPLETE'] as const).map((type) => (
                    <Button
                      key={type}
                      type="button"
                      variant={activeDraft.fulfillmentType === type ? 'default' : 'outline'}
                      onClick={() => updateActiveDraft({
                        fulfillmentType: type,
                        controlledFolio: type === 'PARTIAL' ? '' : activeDraft.controlledFolio,
                      })}
                    >
                      {type === 'PARTIAL' ? 'Parcial' : 'Completo'}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {activeDraft.fulfillmentType === 'COMPLETE' && hasControlled && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Folio de medicamento controlado <span className="text-red-600">*</span>
                </label>
                <Input
                  value={activeDraft.controlledFolio}
                  onChange={(event) => updateActiveDraft({ controlledFolio: event.target.value.toUpperCase() })}
                  placeholder="Ingrese el folio manual de esta receta"
                />
              </div>
            )}

            {activeDraft.fulfillmentType === 'COMPLETE' && hasAntibiotic && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
                El folio consecutivo de antibiótico se asignará a esta receta al confirmar la venta.
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={save} disabled={saveMutation.isPending} className="flex-1">
                {saveMutation.isPending
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Check className="mr-2 h-4 w-4" />}
                Guardar y continuar al pago
              </Button>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saveMutation.isPending}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <NewDoctorModal
        open={showNewDoctor}
        onOpenChange={setShowNewDoctor}
        onCreated={(doctor) => {
          updateActiveDraft({ doctor })
          queryClient.invalidateQueries({ queryKey: ['catalogSearch', 'doctors'] })
        }}
      />
    </>
  )
}
